<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

import { assert } from '@/core/utils/assert'
import { useTeamStore } from '@/pinia/useTeamStore'
import { getDesignatedPlans, type DesignatedPlans } from '@/backend/darwin/getDesignatedPlans'
import { startPlanChange } from '@/backend/darwin/startPlanChange'
import { pluralize } from '@/core/utils/pluralize'
import { useIntercom } from '@/composables/useIntercom'
import { useToast } from '@/uiKit/Toast/useToast'
import Spinner from '@/uiKit/Spinner/Spinner.vue'
import DarwinButton from '@/uiKit/Button/DarwinButton.vue'
import DarwinAvatar from '@/uiKit/Avatar/DarwinAvatar.vue'

import { CustomerPlan } from './types'
import { formatCurrency } from './billingUtils'
import { Resource } from './planTypes'
import { usePlanLoader } from './usePlanLoader'
import { usePlanStore } from './usePlanStore'
import { useUpgradeLocation } from './useUpgradeLocation'
import PlanCard from './PlanCard.vue'
import PlansSelfServePlan from './PlansSelfServePlan.vue'
import PlansSelfServeUsage from './PlansSelfServeUsage.vue'
import { useAuth } from '@/modules/Auth/useAuth'
import NoAccessToCustomer from './LBBNoAccessToCustomer.vue'

const designatedPlans = ref<DesignatedPlans>({
  aws: null,
  free_tier: null,
  starter_monthly: null,
  starter_yearly: null,
})

const { isAuthorized } = useAuth()
const planStore = usePlanStore()

const canManageCustomer = computed(() => isAuthorized('manage_customer'))

const currentPlan = computed(() =>
  planStore.hasCustomerPlanLoaded ? planStore.customerPlan : null,
)

const isSelfServe = computed(
  () =>
    currentPlan.value?.plan_id === designatedPlans.value.free_tier ||
    currentPlan.value?.plan_id === designatedPlans.value.starter_monthly ||
    currentPlan.value?.plan_id === designatedPlans.value.starter_yearly,
)

const upgradeLocation = useUpgradeLocation()

const freeTier = computed(() => ({
  isCurrent: currentPlan.value?.plan_id === designatedPlans.value.free_tier,
  name: 'Basic',
  priceLabel: 'Free',
  priceInterval: null,
  description: 'Annotate up to 1,000 files and test out V7 for free',
  previousPlanName: null,
  id: designatedPlans.value.free_tier,
  features: ['1,000 files', '3 seats', '1 workspace'],
}))

const starter = computed(() => {
  const isCurrentMonthly = planStore.customerPlan?.plan_id === designatedPlans.value.starter_monthly
  const isCurrentYearly = planStore.customerPlan?.plan_id === designatedPlans.value.starter_yearly

  return {
    isCurrent: isCurrentMonthly || isCurrentYearly,
    name: 'Starter',
    priceLabel: isCurrentYearly ? formatCurrency(5000) : formatCurrency(499),
    priceInterval: isCurrentYearly ? ('year' as const) : ('month' as const),
    description: 'Best for individual teams or single deployments working with images',
    id: isCurrentMonthly
      ? designatedPlans.value.starter_monthly
      : designatedPlans.value.starter_yearly,
    features: [
      '50,000 files',
      'Assistance from our award winning Support Team',
      'Access to the Sampling and Logic Stage',
      'Purchase more seats as add-ons, anytime',
    ],
  }
})

const business = {
  name: 'Business',
  description: 'High-scale training data operations & deployment across any specialised modality',
  features: [
    '1M files',
    'All data modalities: videos, medical files, and high res images',
    '15 Seats and 1 Workspace',
    '3 Storage Integrations',
    'Access to all of our workflow stages',
    'A dedicated Customer Success Manager',
  ],
}

const pro = {
  name: 'Pro',
  description: 'Best for powering collaborative workflows across multiple departments',
  features: [
    '4M files',
    '60 Seats and 4 Workspaces',
    '9 Storage Integrations',
    '8 Solutions Engineering Hours',
  ],
}

const enterprise = {
  name: 'Enterprise',
  description: 'Best for powering collaborative workflows across multiple departments',
  features: [
    '10M+ files',
    '150 Seats and 10 Workspaces',
    '30 Storage Integrations',
    '20 Solutions Engineering Hours',
  ],
}

const teamStore = useTeamStore()

const planLoader = usePlanLoader()

const route = useRoute()
const uiState = ref<'loading' | 'upgrading' | 'ready'>('loading')

const toast = useToast()

let poller: number | null

const pollForPlanChange = async (): Promise<void> => {
  const expectedPlanId = route.query.plan as string | undefined
  const isCancelled = 'cancelled' in route.query
  const currentPlanId = currentPlan.value?.plan_id

  if (isCancelled) {
    toast.warning({ meta: { title: 'Your plan upgrade has been cancelled' } })
  }

  if (isCancelled || !expectedPlanId || expectedPlanId === currentPlanId) {
    uiState.value = 'ready'
    return
  }

  uiState.value = 'upgrading'
  await planLoader.loadPlan()
  poller = window.setTimeout(pollForPlanChange, 5000)
}

const loadAndSetDesignatedPlans = async (): Promise<void> => {
  const response = await getDesignatedPlans(assert(teamStore.currentTeam).slug)
  if ('data' in response) {
    designatedPlans.value = response.data
  }
}

onMounted(async () => {
  uiState.value = 'loading'
  await Promise.all([loadAndSetDesignatedPlans(), planLoader.loadPlan()])
  pollForPlanChange()
})

onBeforeUnmount(() => {
  if (poller !== null) {
    window.clearTimeout(poller)
    poller = null
  }
})

const spinnerLabel = computed(() =>
  uiState.value === 'upgrading' ? 'Upgrading your plan' : 'Loading your plan',
)

const startChange = async (interval: 'monthly' | 'yearly'): Promise<void> => {
  if (!teamStore.currentTeam || !designatedPlans.value) {
    return
  }

  const planId =
    interval === 'monthly'
      ? designatedPlans.value.starter_monthly
      : designatedPlans.value.starter_yearly
  if (!planId) {
    return
  }

  // This will redirect us to stripe checkout
  // by the time we come back here, the plan should already be updated on the backend
  // but there is no guarantee for it yet, so the user may have to refresh the page to see the
  // change.
  // We will introduce polling in a later ticket.
  const response = await startPlanChange(teamStore.currentTeam.slug, planId)
  if ('data' in response) {
    window.location.assign(response.data.url)
  }
}

type SupportedResource = Extract<Resource, 'workspace' | 'user' | 'worker' | 'manager'>

const isSupportedResouce = (r: Resource): r is SupportedResource =>
  [Resource.WORKSPACE, Resource.USER, Resource.WORKER, Resource.MANAGER].some((sr) => sr === r)

const resourceToFormattedPairsMap = {
  [Resource.WORKSPACE]: ['workspace', 'workspaces'],
  [Resource.USER]: ['user license', 'user licenses'],
  [Resource.WORKER]: ['worker license', 'worker licenses'],
  [Resource.MANAGER]: ['manager license', 'manager licenses'],
} as const

const formattedPlanLicenses = computed(() => {
  const results: string[] = []

  currentPlan.value?.plan_licenses.forEach((license) => {
    if (!isSupportedResouce(license.resource)) {
      return
    }
    const [formattedSingular, formattedPlural] = resourceToFormattedPairsMap[license.resource]
    results.push(pluralize(license.limit, formattedSingular, formattedPlural))
  })

  return results
})

const formattedPlanAddons = computed(() => {
  const results: string[] = []

  currentPlan.value?.plan_addon_licenses.map((addon) => {
    if (!isSupportedResouce(addon.resource)) {
      return
    }
    const [formattedSingular, formattedPlural] = resourceToFormattedPairsMap[addon.resource]
    const pluralizedResource = pluralize(addon.limit, formattedSingular, formattedPlural)

    results.push(pluralizedResource)
  })

  return results
})

const planAndAddonCardDescriptors = computed(() => [
  {
    header: 'Current plan',
    icon: 'duotone-star' as const,
    title: currentPlan.value?.name,
    properties: formattedPlanLicenses.value,
    buttonText: 'Explore plans',
    variant: 'primary' as const,
    location: upgradeLocation.value.OPEN_SIGNUP_ENTERPRISE_PLAN,
  },
  {
    header: 'Add-on',
    icon: 'duotone-person' as const,
    title: 'Admin License',
    properties: ['Purchase an extra license'],
    buttonText: 'Explore plans',
    variant: 'secondary' as const,
    location: upgradeLocation.value.OPEN_SIGNUP_ADMIN_ADDON_PURCHASE,
  },
  {
    header: 'Add-on',
    icon: 'mono-workspace' as const,
    title: 'Workspace',
    properties: ['Purchase an extra workspace'],
    buttonText: 'Explore plans',
    variant: 'secondary' as const,
    location: upgradeLocation.value.WORKSPACE_ADDON_PURCHASE,
  },
])

const isFreeTierPlan = computed(() => planStore.customerPlan?.name === CustomerPlan.FREE_TIER)
const { intercom } = useIntercom()
const showIntercom = (): void => {
  if (isFreeTierPlan.value) {
    return
  }
  intercom?.show()
}

const userDistribution = computed(
  () =>
    planStore.customerPlan?.usage.licenses_distribution.find((x) => x.resource === Resource.USER)
      ?.distribution || [],
)
const maxMembersInAWorkspace = computed(() =>
  Math.max(...userDistribution.value.map((x) => x.count)),
)

const formattedUserDistribution = computed(() => {
  if (!userDistribution.value) {
    return []
  }
  const teams = teamStore.currentTeam
    ? [teamStore.currentTeam, ...teamStore.currentTeam.clients]
    : []

  return teams
    .map((team) => {
      const teamDistribution = userDistribution.value.find((x) => x.workspace_id === team.id)
      return {
        id: team.id,
        name: team.name,
        image: team.image,
        status: 'active',
        members: teamDistribution?.count ?? 0,
        memberRatio: Math.round(
          ((teamDistribution?.count ?? 0) * 100) / maxMembersInAWorkspace.value,
        ),
      }
    })
    .sort((a, b) => b.members - a.members)
})
</script>

<template>
  <template v-if="canManageCustomer">
    <div
      v-if="uiState === 'ready'"
      class="lbb"
    >
      <div
        v-if="isSelfServe"
        class="lbb__plans"
      >
        <PlansSelfServePlan
          :name="freeTier.name"
          :description="freeTier.description"
          price-label="Free"
          :price-interval="null"
          :features="freeTier.features"
          :is-current="freeTier.isCurrent"
          :talk-to-sales-url="freeTier.isCurrent ? null : upgradeLocation.DOWNGRADE"
        />
        <PlansSelfServePlan
          :name="starter.name"
          :description="starter.description"
          :price-label="starter.priceLabel"
          :price-interval="starter.priceInterval"
          :features="starter.features"
          :is-current="starter.isCurrent"
          :previous-name="freeTier.name"
          @upgrade-monthly="startChange('monthly')"
          @upgrade-yearly="startChange('yearly')"
        />
        <PlansSelfServePlan
          :name="business.name"
          :description="business.description"
          :features="business.features"
          :previous-name="starter.name"
          price-label="Contact sales"
          :talk-to-sales-url="upgradeLocation.BUSINESS"
        />
        <PlansSelfServePlan
          :name="pro.name"
          :description="pro.description"
          :features="pro.features"
          :previous-name="business.name"
          price-label="Contact sales"
          :talk-to-sales-url="upgradeLocation.PRO"
        />
        <PlansSelfServePlan
          :name="enterprise.name"
          :description="enterprise.description"
          :features="enterprise.features"
          :previous-name="pro.name"
          price-label="Contact sales"
          :talk-to-sales-url="upgradeLocation.ENTERPRISE"
        />
      </div>
      <template v-else>
        <div class="plans">
          <PlanCard
            v-for="plan in planAndAddonCardDescriptors"
            :key="plan.title"
            class="plans__item"
            :header="plan.header"
            :icon="plan.icon"
            :title="plan.title || ''"
            :properties="plan.properties"
            :variant="plan.variant"
          >
            <template #footer>
              <DarwinButton
                full-width
                kind="outline"
                flair="rounded"
                size="sm"
                :to="isFreeTierPlan ? plan.location : undefined"
                @click="showIntercom"
              >
                {{ plan.buttonText }}
              </DarwinButton>
            </template>
          </PlanCard>
        </div>
        <div class="banner">
          <h2 class="banner__title">Add-ons</h2>
          <ul class="addons-list addons-list--left">
            <li
              v-for="addon in formattedPlanAddons"
              :key="addon"
            >
              <Icon name="mono-check" /> {{ addon }}
            </li>
          </ul>
          <DarwinButton
            kind="primary"
            flair="rounded"
            size="sm"
            target="_blank"
            :to="upgradeLocation.OPEN_SIGNUP_TALK_TO_SALES_BANNER"
            @click="showIntercom"
          >
            Talk to sales
          </DarwinButton>
        </div>
      </template>

      <PlansSelfServeUsage class="lbb__usage" />
      <table
        v-if="formattedUserDistribution.length > 1"
        class="workspaces"
      >
        <thead class="workspaces__header">
          <tr>
            <th>Workspace name</th>
            <th>Status</th>
            <th colspan="2">Members</th>
          </tr>
        </thead>
        <tbody class="workspaces__body">
          <tr
            v-for="workspace in formattedUserDistribution"
            :key="workspace.id"
          >
            <td>
              <div class="workspaces__name">
                <DarwinAvatar
                  :id="workspace.id"
                  :url="workspace.image?.thumbnail_url"
                  :name="workspace.name"
                  class="workspaces__avatar"
                />
                <span>{{ workspace.name }}</span>
              </div>
            </td>
            <td>
              <Badge
                :label="workspace.status"
                class="status-badge"
                size="medium"
              >
                <template #prefix-icon>
                  <div
                    v-if="workspace.status === 'active'"
                    class="dot"
                  />
                </template>
              </Badge>
            </td>
            <td class="workspace__members">
              {{ workspace.members }}
            </td>
            <td>
              <span
                class="ratio-bar"
                :class="{ 'ratio-bar--disabled': workspace.memberRatio === 0 }"
                :style="{ width: `${workspace.memberRatio}%` }"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div
      v-else
      class="lbb-loader"
    >
      <Spinner
        size="md"
        color="dark"
        :label="spinnerLabel"
      />
      {{ spinnerLabel }}
    </div>
  </template>
  <NoAccessToCustomer v-else />
</template>

<style lang="scss" scoped>
.lbb-loader {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: var(--size-2);
  color: var(--color-text-subtle);
  flex-grow: 1;
}

.lbb {
  display: flex;
  flex-direction: column;
  gap: var(--size-8);

  position: relative;

  &__plans {
    display: flex;
    flex-direction: row;
    gap: var(--size-2);
  }

  &__usage {
    position: sticky;
    left: 0;
  }
}

.plans {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;

  &__item {
    flex: 1;
  }
}

.banner {
  max-width: min(50vw, 600px);
  min-width: fit-content;
  background-image: url('./PlansLBBGradient.png');
  background-size: cover;
  padding: 24px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border-radius: 16px;
  padding-left: 24px;
  padding-right: 44px;
  border: 1px solid var(--color-border-default);
  margin-bottom: 24px;

  &__title {
    @include typographyBoldBody400;
    color: var(--color-text-default);
  }
}

.addons-list {
  list-style: none;
  padding: 0;
  margin: 0;
  @include typographyRegularBody200;
  color: var(--color-text-subtle);
  display: flex;
  flex-direction: column;
  gap: 4px;

  > li {
    display: flex;
    align-items: center;
    gap: 4px;
    justify-content: flex-start;
  }
}

.workspaces {
  max-width: min(50vw, 600px);
  min-width: fit-content;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid var(--color-border-default);
  border-radius: 12px;
  margin-bottom: 48px;
  color: var(--color-text-subtle);
  text-align: left;

  td {
    border-bottom: 1px solid var(--color-border-default);
    padding: 12px;
    vertical-align: middle;

    &:nth-child(1) {
      width: 312px;
    }

    &:nth-child(2) {
      width: 120px;
    }

    &:first-child {
      padding-left: 16px;
    }

    &:last-child {
      padding-right: 16px;
    }

    .status-badge {
      justify-content: flex-start;
    }

    &.workspace__members {
      @include typographyRegularBody100;
      width: 0;
      text-align: right;
      padding-right: 0;
    }
  }

  th {
    padding: 8px 12px;
    border-bottom: 1px solid var(--color-border-default);
  }

  &__header {
    background: var(--color-surface-secondary);
    border-radius: 0px 0px 12px 12px;
    @include typographyBoldBody200;
    color: var(--color-text-default);

    tr:first-child {
      th {
        &:first-child {
          border-top-left-radius: 12px;
          padding-left: 16px;
        }

        &:last-child {
          border-top-right-radius: 12px;
          padding-right: 16px;
        }
      }
    }
  }

  &__body {
    tr:last-child {
      td {
        border-bottom: 0;

        &:first-child {
          border-bottom-left-radius: 12px;
        }

        &:last-child {
          border-bottom-right-radius: 12px;
        }
      }
    }
  }

  &__avatar {
    margin-right: 12px;
    font-size: 24px;
    width: 24px;
    height: 24px;
    color: var(--color-text-default);
  }

  &__name {
    display: flex;
    align-items: center;
  }
}

.dot {
  width: 6px;
  height: 6px;
  border-radius: 100%;
  background: var(--color-text-success);
}

.ratio-bar {
  display: inline-block;
  height: 6px;
  min-width: 10px;
  background: var(--color-icon-primary);
  border-radius: 0px 3px 3px 0px;

  &--disabled {
    background: var(--color-icon-gray-subtle);
  }
}
</style>
